import React from "react";
import Animated, { useSharedValue } from "react-native-reanimated";

import Icon from "$src/core/components/atoms/Icon";
import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import type { IconName } from "$src/core/models/icon";

import $, { useAnimatedStyles } from "./ProfileSelectorButton.styles";

type Props = {
  name?: string;
  tint: string;
  icon?: IconName | null;
  hasLightIcon?: boolean;
  onPress: () => void;
  onLongPress?: () => void;
  onFocus?: () => void;
  focusable?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  testID?: string;
};

const ProfileSelectorButton: React.FC<Props> = ({
  name,
  tint,
  icon = "person-outline",
  hasLightIcon,
  onPress,
  onLongPress,
  onFocus,
  focusable,
  accessibilityLabel,
  accessibilityHint,
  testID,
}) => {
  $.useVariants({ hasLightIcon });

  const isFocused = useSharedValue(false);

  const { circleStyle } = useAnimatedStyles({
    isFocused,
  });

  return (
    <Pressable
      style={$.container}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? name}
      accessibilityHint={accessibilityHint}
      onPress={onPress}
      onLongPress={onLongPress}
      onFocus={() => {
        isFocused.set(true);
        onFocus?.();
      }}
      onBlur={() => isFocused.set(false)}
      focusable={focusable}
      testID={testID}
    >
      <Animated.View style={[$.circle, { backgroundColor: tint }, circleStyle]}>
        {icon && <Icon name={icon} style={$.icon} />}
      </Animated.View>

      {name != null && (
        <Text
          preset="heading5"
          accessibilityRole="text"
          accessible={name.length > 0}
          style={$.name}
        >
          {name.length > 0 ? name : "\u00A0"}
        </Text>
      )}
    </Pressable>
  );
};

export default ProfileSelectorButton;
